import { Component, h, Prop, State } from '@stencil/core';
import { Unsubscribe } from '@stencil/redux';
import { store } from '../../redux/store';
import { postItemDetails } from '../../api';
import { callIfDepsChanged } from '../../utils/utils';
import { BookingDetails, Cart, ItemDetails, PriceOptions, TimeSlotPicker, Translations, Widget } from '../../redux/types';
import { RootState } from '../../redux/reducers';
import { ComponentView } from '../../types/types';
import { makeTimePicker } from './utils/utils';

@Component({
  tag: 'rentware-article-booking',
  shadow: true,
  styleUrl: 'templates/style.sass',
})
export class RentwareArticleBooking {
  @Prop() articleId: string;
  @Prop() view: ComponentView = ComponentView.Default;

  @Prop({ mutable: true }) timePicker: TimeSlotPicker;
  @Prop({ mutable: true }) bookingDetails: BookingDetails;
  @Prop({ mutable: true }) widget: Widget;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) cartState: Cart;
  @Prop({ mutable: true }) translations: Translations;
  @Prop({ mutable: true }) loadedComponents;
  @Prop({ mutable: true }) priceOptions: PriceOptions;
  @Prop({ mutable: true }) reservationPopup: ComponentView[] = [];

  @State() itemDetails: ItemDetails;
  @State() selectedVariationId?: string;
  @State() selectedMileageId?: string;

  unsubscribe!: Unsubscribe;

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      if (state.app.locale) {
        callIfDepsChanged(
          RentwareArticleBooking.name + '-postItemDetails-' + this.articleId,
          () =>
            postItemDetails(
              this.articleId,
              state.orderDetails?.id,
              state.app.locale,
              null,
              false,
              data => {
                this.itemDetails = data;
                if (this.itemDetails.variation) {
                  this.selectedVariationId = this.itemDetails.variation.items[0].id;
                }
                if (!!this.itemDetails.mileageOptions) {
                  this.selectedMileageId = this.itemDetails.mileageOptions[0].id;
                }
              },
              undefined,
            ),
          [state.orderDetails?.id, state.app.locale],
          !this.itemDetails, // if state is empty call anyway
        );
      }

      return {
        cartState: state.cart,
        bookingDetails: state.bookingDetails,
        widget: state.widget,
        timePicker: makeTimePicker(state?.widget?.timePicker),
        locale: state.app.locale,
        translations: state.system.translations,
        reservationPopup: state.system.widgetConfig?.advanced?.general?.reservationPopup,
        priceOptions: {
          currency: state.system.currency,
          decimalSeparator: state.system.pricing?.format.decimalSeparator,
          currencyPosition: state.system.pricing?.format.currencyPosition,
          displayNet: state.system.pricing?.priceDisplay === 'net',
          priceCalculation: state.system.pricing?.itemPriceCalculation,
        },
        loadedComponents: state.app.loadedComponents,
      };
    });
  }

  disconnectedCallback() {
    this.unsubscribe();
  }

  render() {
    if (!this.widget || !this.locale || !this.itemDetails) {
      return null;
    }

    if (this.view === ComponentView.Slots) {
      return (
        <rtr-ready-wrapper
          renderOnReady={() => {
            return (
              <div class="ArticleBookingContainerSlots">
                <rentware-slots itemId={this.articleId} />
              </div>
            );
          }}
        />
      );
    }

    if (this.view === ComponentView.Cards) {
      return (
        <rtr-ready-wrapper
          renderOnReady={() => {
            return (
              <div class="ArticleBookingContainer">
                <rentware-article-booking-cards-engine articleId={this.articleId} bookingDetails={this.bookingDetails} />
              </div>
            );
          }}
        />
      );
    }

    if (this.view === ComponentView.Calendar) {
      return (
        <rtr-ready-wrapper
          renderOnReady={() => {
            return (
              <div class="ArticleBookingContainer">
                <rentware-article-booking-range-engine
                  articleId={this.articleId}
                  itemDetails={this.itemDetails}
                  selectedVariationId={this.selectedVariationId}
                  selectedMileageId={this.selectedMileageId}
                  bookingDetails={this.bookingDetails}
                  widget={this.widget}
                  timePicker={this.timePicker}
                  locale={this.locale}
                  cartState={this.cartState}
                  translations={this.translations}
                  loadedComponents={this.loadedComponents}
                  priceOptions={this.priceOptions}
                  reservationPopup={this.reservationPopup}
                />
              </div>
            );
          }}
        />
      );
    }

    return (
      <rtr-ready-wrapper
        renderOnReady={() => {
          return (
            <div class="ArticleBookingContainer">
              <rentware-article-booking-engine
                articleId={this.articleId}
                itemDetails={this.itemDetails}
                selectedVariationId={this.selectedVariationId}
                bookingDetails={this.bookingDetails}
                selectedMileageId={this.selectedMileageId}
                widget={this.widget}
                timePicker={this.timePicker}
                locale={this.locale}
                cartState={this.cartState}
                translations={this.translations}
                loadedComponents={this.loadedComponents}
                priceOptions={this.priceOptions}
                reservationPopup={this.reservationPopup}
              />
            </div>
          );
        }}
      />
    );
  }
}
